import { useCallback, useEffect, useRef } from 'react';
import { getMeProfile } from '../../service/MySrcmService';
import { useAuth } from '../../hooks/useAuth';
import { useBatchId } from '../../hooks/useBatchId';
import { UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import { ROOT, SELECT_ROLE } from '../../shared/Routes';
import useUserSession from '../../hooks/useUserSession';
import { getBatches } from '../../service/PreWorkService';
import { DEFAULT_PAGE_SIZE } from '../../shared/Constants';
import { getLoggedInUserRoles } from '../../service/RoleBasedService';
import { Role } from '../../shared/RoleBasedTypes';

interface LoginResponse {
	data?: {
		access_token?: string;
	};
}

export const HFNAuth = () => {
	const { setMySrcmProfile, setFirebaseId, setInitiated } = useAuth();
	const {
		setKeycloakToken,
		setMeProfile,
		setSrcmFirebaseId,
		setSelectedRole,
		setAppInitiated,
		setCurrentActiveBatch,
	} = useUserSession();

	const { navigateToRoute, queryParams } = useBatchId();
	const apiCallCount = useRef(0);
	const { srcmId } = useAuth();

	const handleAuth = (status: boolean) => {
		const element = document.querySelector('hfn-auth') as HTMLElement & {
			handleProfileAuthentication?: (_status: boolean) => void;
		};
		if (element?.handleProfileAuthentication) {
			element.handleProfileAuthentication(status);
		}
	};

	const getProfile = useCallback(async () => {
		if (++apiCallCount.current >= 4) return handleAuth(false);
		const response = await getLoggedInUserRoles({
			loggedInUserSrcmId: srcmId ?? '',
		});

		const roles = response?.rolesDetails ?? [];
		const isLoggedAsPreceptor = roles.some(
			(r: { role: Role }) => r.role.code === UserRole.PRECEPTOR
		);

		try {
			const profile = await getMeProfile();

			if (!profile) {
				// TODO: Handle Unauthorized page here
				return;
			}

			setMeProfile(profile);
			setMySrcmProfile?.(profile);

			if (profile.user_firebase_uid) {
				setSrcmFirebaseId(profile.user_firebase_uid);
				setFirebaseId?.(profile.user_firebase_uid);
			}

			if (profile.ref && profile.user_firebase_uid) {
				const batchData = await getBatches({
					functionarySrcmId: profile.ref,
					pageIndex: 0,
					pageSize: DEFAULT_PAGE_SIZE,
					batchFilter: { status: 'ACTIVE' },
				});

				if (
					batchData.batches &&
					batchData.batches.length > 0 &&
					batchData.batches[0].id
				) {
					const currentBatch = batchData.batches[0];
					setCurrentActiveBatch(currentBatch);
				}
			}

			if (profile.is_prefect === false) {
				setSelectedRole?.(UserRole.APPLICANT);
			}

			setAppInitiated(true);
			setInitiated?.(true);
			handleAuth(true);
			['state', 'session_state', 'iss', 'code'].forEach((param) =>
				queryParams.delete(param)
			);

			if (isLoggedAsPreceptor) {
				navigateToRoute(SELECT_ROLE);
				return;
			}
			navigateToRoute(ROOT);
		} catch {
			handleAuth(false);
		}
	}, [
		setMeProfile,
		setMySrcmProfile,
		setAppInitiated,
		setInitiated,
		navigateToRoute,
		setSrcmFirebaseId,
		setFirebaseId,
		setCurrentActiveBatch,
		setSelectedRole,
		queryParams,
		srcmId,
	]);

	useEffect(() => {
		const loadHFNAuth = async () => {
			try {
				await import('hfnauth/main');
				const element = document.querySelector('hfn-auth') as HTMLElement & {
					loginCallback?: (_res: LoginResponse) => Promise<boolean>;
				};

				if (!element) return;

				element.loginCallback = async (res: LoginResponse) => {
					const accessToken = res?.data?.access_token;
					if (!accessToken) {
						handleAuth(false);
						return false;
					}

					setKeycloakToken(accessToken);
					await getProfile();
					return true;
				};
			} catch {
				// Handle import error silently
			}
		};

		void loadHFNAuth();
	}, [getProfile, setKeycloakToken]);

	return <></>;
};
